import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { AlertCircle, Plus, Check, MessageCircle } from 'lucide-react';

const CATEGORIES = [
  { v: 'payroll', label: 'Payroll' },
  { v: 'leave', label: 'Leave / Time-off' },
  { v: 'facilities', label: 'Facilities / Workplace' },
  { v: 'harassment', label: 'Harassment / Conduct' },
  { v: 'tech', label: 'Tech / IT' },
  { v: 'other', label: 'Other' },
];

export default function Issues() {
  const { user } = useAuth();
  const isHr = ['admin', 'hr'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ category: 'other', subject: '', description: '' });

  const load = async () => {
    try {
      const { data } = await api.get('/issues');
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject.trim()) return toast.error('Subject is required');
    if (!form.description.trim()) return toast.error('Description is required');
    setSaving(true);
    try {
      await api.post('/issues', form);
      toast.success('Issue reported — HR has been notified');
      setOpen(false);
      setForm({ category: 'other', subject: '', description: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resolve = async (iid) => {
    const note = window.prompt('Resolution note (optional):');
    try {
      await api.patch(`/issues/${iid}/resolve`, { note: note || null });
      toast.success('Issue resolved');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="issues-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Support</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">
            {isHr ? 'Reported Issues' : 'Report an Issue'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isHr ? `${items.length} issue${items.length !== 1 ? 's' : ''} reported across the company. You can also raise your own.`
                  : 'Anything bothering you at work? Let HR know — it goes straight to their inbox.'}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="report-issue-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
              <Plus className="w-4 h-4 mr-2" /> Report a new issue
            </Button>
          </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="report-issue-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Report an issue</DialogTitle>
                <p className="text-sm text-slate-500">Your name and email are included so HR can follow up.</p>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Category *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="issue-category" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Subject *</Label>
                  <Input data-testid="issue-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="A short headline" className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Description *</Label>
                  <Textarea data-testid="issue-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} placeholder="What happened? When did it start? What should HR know?" className="rounded-xl mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button data-testid="submit-issue" onClick={submit} disabled={saving} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {saving ? 'Submitting…' : 'Report issue'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400" data-testid="no-issues">
            <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
            {isHr ? 'No issues reported yet.' : 'You haven\'t reported any issues. We hope it stays that way!'}
          </div>
        )}
        {items.map(i => (
          <div key={i.id} data-testid={`issue-${i.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-semibold text-slate-900">{i.subject}</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{i.category}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${
                    i.status === 'resolved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                            : 'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>{i.status}</span>
                </div>
                {isHr && <div className="text-xs text-slate-500 mt-1">{i.employee_name} · {i.employee_email}</div>}
                <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{i.description}</div>
                <div className="text-xs text-slate-400 mt-2">Reported {new Date(i.created_at).toLocaleString()}</div>
                {i.resolution_note && (
                  <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg p-2 border border-emerald-100">
                    <b>HR resolution:</b> {i.resolution_note}
                  </div>
                )}
              </div>
              {isHr && i.status !== 'resolved' && (
                <Button size="sm" onClick={() => resolve(i.id)} data-testid={`resolve-issue-${i.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-8 text-xs flex-shrink-0">
                  <Check className="w-3 h-3 mr-1" /> Mark resolved
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
